import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaTruck,
  FaShippingFast,
  FaMapMarkerAlt,
  FaShieldAlt,
  FaCheckCircle,
  FaRoute,
} from "react-icons/fa";

export default function ServicosPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Serviços — Usina Rosa";
  }, []);

  const features = [
    {
      icon: <FaRoute className="text-emerald-700 text-2xl" />,
      title: "Rastreamento em tempo real",
      desc: "Frota própria equipada com sistema 24/7 para visibilidade ponta-a-ponta do seu pedido.",
    },
    {
      icon: <FaMapMarkerAlt className="text-emerald-700 text-2xl" />,
      title: "Cobertura em SP",
      desc: "Atendimento em todo o estado de São Paulo, com avaliação sob consulta para demais regiões.",
    },
    {
      icon: <FaTruck className="text-emerald-700 text-2xl" />,
      title: "Retirada na usina (opcional)",
      desc: "Flexibilidade operacional: retire diretamente na usina quando for mais conveniente.",
    },
    {
      icon: <FaShippingFast className="text-emerald-700 text-2xl" />,
      title: "Entrega segura e ágil",
      desc: "Compromisso com prazo, integridade da carga e atendimento dedicado ao seu negócio.",
    },
    {
      icon: <FaShieldAlt className="text-emerald-700 text-2xl" />,
      title: "Higienização certificada",
      desc: "Frota submetida a processos rigorosos conforme normas vigentes e boas práticas do setor.",
    },
    {
      icon: <FaCheckCircle className="text-emerald-700 text-2xl" />,
      title: "Tanques limpos e descontaminados",
      desc: "Limpeza, higienização e descontaminação periódicas asseguram qualidade e segurança do produto.",
    },
  ];

  const stepItem = (i) => ({
    initial: { opacity: 0, y: 14 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.45, delay: i * 0.06 },
  });

  return (
    <main className="bg-white">
      {/* HERO */}
      <section className="relative pt-24 md:pt-28 pb-16 md:pb-24 px-4 md:px-6 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-emerald-50" />
          <motion.div
            aria-hidden
            className="absolute -top-20 -right-16 w-60 md:w-72 h-60 md:h-72 rounded-full blur-3xl"
            style={{
              background:
                "radial-gradient(circle at center, rgba(16,185,129,0.35), transparent 60%)",
            }}
            initial={{ opacity: 0.6, scale: 0.9 }}
            animate={{ opacity: 0.9, scale: 1 }}
            transition={{ duration: 4, repeat: Infinity, repeatType: "reverse" }}
          />
          <motion.div
            aria-hidden
            className="absolute -bottom-24 -left-20 w-72 md:w-96 h-72 md:h-96 rounded-full blur-[70px] md:blur-[80px]"
            style={{
              background:
                "radial-gradient(circle at center, rgba(52,211,153,0.3), transparent 60%)",
            }}
            initial={{ opacity: 0.5, scale: 1 }}
            animate={{ opacity: 0.8, scale: 1.05 }}
            transition={{ duration: 5, repeat: Infinity, repeatType: "reverse" }}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                "repeating-linear-gradient(135deg, rgba(16,185,129,0.06) 0px, rgba(16,185,129,0.06) 2px, transparent 2px, transparent 26px)",
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-3xl md:text-5xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-emerald-700 via-teal-700 to-lime-700"
          >
            Logística que entrega confiança
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.5 }}
            className="mt-3 md:mt-4 text-base md:text-lg lg:text-xl text-slate-700 max-w-3xl mx-auto"
          >
            Frota própria com rastreamento em tempo real, atendimento em todo estado de São Paulo,
            opção de retirada na usina e protocolos rígidos de higienização.
          </motion.p>
        </div>
      </section>

      {/* DIFERENCIAIS */}
      <section className="relative px-4 md:px-6 pb-10 md:pb-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              {...stepItem(i)}
              className="rounded-2xl border border-emerald-100 bg-white/85 backdrop-blur-sm p-4 sm:p-5 md:p-6 shadow-sm hover:shadow-md"
            >
              <div className="flex items-start gap-3 md:gap-4">
                <div className="shrink-0 rounded-xl bg-gradient-to-br from-emerald-50 via-teal-50 to-lime-50 p-2.5 md:p-3">
                  {f.icon}
                </div>
                <div>
                  <h2 className="text-base md:text-lg font-semibold text-emerald-900">
                    {f.title}
                  </h2>
                  <p className="mt-1 text-sm md:text-base text-slate-700 leading-relaxed">
                    {f.desc}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section className="px-4 md:px-6 py-12 md:py-16">
        <div className="max-w-7xl mx-auto">
          <motion.h3
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
            className="text-xl md:text-3xl font-extrabold text-emerald-900 text-center"
          >
            Como garantimos uma entrega segura e ágil
          </motion.h3>

          <div className="mt-8 md:mt-10 grid grid-cols-1 md:grid-cols-4 gap-5 md:gap-6">
            <motion.div
              {...stepItem(0)}
              className="rounded-2xl bg-white/85 border border-emerald-100 p-4 md:p-6 shadow-sm"
            >
              <div className="flex items-center gap-2.5 md:gap-3 text-emerald-800 font-semibold text-sm md:text-base">
                <FaMapMarkerAlt /> Solicitação
              </div>
              <p className="mt-2 text-slate-700 text-sm leading-relaxed">
                Recebemos seu pedido e validamos janela de entrega e documentação.
              </p>
            </motion.div>

            <motion.div
              {...stepItem(1)}
              className="rounded-2xl bg-white/85 border border-emerald-100 p-4 md:p-6 shadow-sm"
            >
              <div className="flex items-center gap-2.5 md:gap-3 text-emerald-800 font-semibold text-sm md:text-base">
                <FaTruck /> Programação
              </div>
              <p className="mt-2 text-slate-700 text-sm leading-relaxed">
                Alocamos veículo e motorista conforme o tipo de produto e destino.
              </p>
            </motion.div>

            <motion.div
              {...stepItem(2)}
              className="rounded-2xl bg-white/85 border border-emerald-100 p-4 md:p-6 shadow-sm"
            >
              <div className="flex items-center gap-2.5 md:gap-3 text-emerald-800 font-semibold text-sm md:text-base">
                <FaRoute /> Rastreamento
              </div>
              <p className="mt-2 text-slate-700 text-sm leading-relaxed">
                Acompanhamento em tempo real do trajeto com atualizações proativas.
              </p>
            </motion.div>

            <motion.div
              {...stepItem(3)}
              className="rounded-2xl bg-white/85 border border-emerald-100 p-4 md:p-6 shadow-sm"
            >
              <div className="flex items-center gap-2.5 md:gap-3 text-emerald-800 font-semibold text-sm md:text-base">
                <FaCheckCircle /> Entrega & checklist
              </div>
              <p className="mt-2 text-slate-700 text-sm leading-relaxed">
                Conferência, lacre e registro fotográfico quando aplicável, encerrando a OS.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CAPACIDADES DA FROTA – NOVO ESTILO */}
      <section className="px-0 py-12 md:py-14">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
          <motion.h5
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
            className="text-emerald-900 text-xl md:text-2xl font-bold mb-5 md:mb-6 text-center"
          >
            Capacidades da frota
          </motion.h5>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="rounded-3xl shadow-lg"
          >
            <img
              src="/images/capacidades-frota.jpg"
              alt="Capacidades de carga por configuração: Truck, Carretas, Bitrem e Rodotrem"
              className="
                w-full
                h-[220px]
                sm:h-[320px]
                md:h-[480px]
                lg:h-[600px]
                xl:h-[720px]
                object-contain
                object-center
                rounded-3xl
              "
            />
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 md:px-6 pb-20 md:pb-24">
        <div className="max-w-5xl mx-auto">
          <motion.h3
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
            className="text-xl md:text-3xl font-extrabold text-emerald-900 text-center"
          >
            Dúvidas frequentes
          </motion.h3>

          <div className="mt-5 md:mt-6 divide-y divide-emerald-100 rounded-2xl border border-emerald-100 bg-white/80 backdrop-blur-sm">
            <details className="group p-4 md:p-6">
              <summary className="cursor-pointer list-none font-semibold text-emerald-900 flex items-center justify-between text-sm md:text-base">
                Como funciona o agendamento de entrega?
                <span className="transition-transform group-open:rotate-180">▾</span>
              </summary>
              <p className="mt-2 text-slate-700 text-sm md:text-base">
                Validamos janela de recebimento, documentação e requisitos de segurança do local. Em seguida,
                programamos o veículo e o motorista adequados ao produto e destino.
              </p>
            </details>

            <details className="group p-4 md:p-6">
              <summary className="cursor-pointer list-none font-semibold text-emerald-900 flex items-center justify-between text-sm md:text-base">
                Posso acompanhar o pedido em tempo real?
                <span className="transition-transform group-open:rotate-180">▾</span>
              </summary>
              <p className="mt-2 text-slate-700 text-sm md:text-base">
                Sim. Nossa frota possui rastreamento 24/7. Oferecemos atualizações proativas do trajeto e
                confirmação de entrega com checklist.
              </p>
            </details>

            <details className="group p-4 md:p-6">
              <summary className="cursor-pointer list-none font-semibold text-emerald-900 flex items-center justify-between text-sm md:text-base">
                Atendem fora do estado de São Paulo?
                <span className="transition-transform group-open:rotate-180">▾</span>
              </summary>
              <p className="mt-2 text-slate-700 text-sm md:text-base">
                Nossa cobertura é todo o estado de São Paulo. Para outras regiões, avaliamos sob consulta
                considerando distância, demanda e prazos.
              </p>
            </details>

            <details className="group p-4 md:p-6">
              <summary className="cursor-pointer list-none font-semibold text-emerald-900 flex items-center justify-between text-sm md:text-base">
                Como vocês garantem a qualidade do produto no transporte?
                <span className="transition-transform group-open:rotate-180">▾</span>
              </summary>
              <p className="mt-2 text-slate-700 text-sm md:text-base">
                Os tanques passam por limpeza, higienização e descontaminação periódicas, seguindo normas vigentes.
                Mantemos registros, lacres e, quando aplicável, documentação fotográfica.
              </p>
            </details>

            <details className="group p-4 md:p-6">
              <summary className="cursor-pointer list-none font-semibold text-emerald-900 flex items-center justify-between text-sm md:text-base">
                É possível retirar diretamente na usina?
                <span className="transition-transform group-open:rotate-180">▾</span>
              </summary>
              <p className="mt-2 text-slate-700 text-sm md:text-base">
                Sim. Oferecemos a opção de retirada na usina de forma programada, garantindo agilidade e conformidade.
              </p>
            </details>
          </div>
        </div>
      </section>
    </main>
  );
}
